import { index, jsonb, pgTable, text, timestamp, uuid, varchar } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { id } from './_shared';
import { users } from './users';

// =============================================================================
// CLAIM CORRECTIONS — Phase 2 Lane B (docs/design/fix-architecture-2026-07-08/
// {FIX-ARCHITECTURE.md,BUILD-PLAN.md}, Class 4, closes D12 + the job-side half
// of D11). The polymorphic "that's not right" ledger: the CONFIRM/DISPUTE half
// of the same expand-on-this primitive Class 2/5 use for the ASK half (system
// asks "is this right?" vs. person volunteers "that's not right").
//
// SHAPE + REASONING COPIED WHOLESALE from achievement_alignment_rulings.ts /
// axis_association_rulings.ts (the third instance of this house pattern):
//   1. Append-only, TERMINAL per (userId, sourceType, sourceId) — a corrected
//      claim never re-flags; a person who changes their mind writes a NEW
//      row (a fresh ruling supersedes the old one for read purposes; the
//      prior ruling stays queryable forever, no deletions, ever).
//   2. flaggedText + evidenceSnapshot are frozen at correction time — the
//      EXACT claim text the person was shown, so a later regeneration can
//      never retroactively rewrite what a past correction was actually made
//      against.
//   3. `ruling` is varchar, not a DB enum (this codebase's house convention,
//      proven twice already) — a future ruling value is a data event, not a
//      migration.
//
// Deliberately a POLYMORPHIC sibling to achievement_alignment_rulings, not a
// forced reuse of that structured-row-specific table: the five surfaces this
// covers (gaps list, reasoning/strengths bullets, radar/axis comparison
// job-side, through-line panel, alignment essay per-line) are prose/claim
// SNIPPETS keyed by (sourceType, sourceId), not a single structured
// achievement row.
//
// USER-DRIVEN, NEVER AUTO-FIX (the governing law, restated): the system
// surfaces the claim; the person disputes/confirms it; the ruling is
// terminal; NOTHING here rewrites the source artifact itself — a disputed
// claim renders with an honest strikethrough/chip on the SAME text, and the
// dispute is a wake event (claim-corrected) that feeds THE KEYSTONE HUB
// (apps/api/services/corpus/coverageHub.ts) so re-generation consumes it as
// grounded signal, never a silent rewrite.
//
// ADDITIVE ONLY (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool
// (docs/design/fix-architecture-2026-07-08/phase2b-neon.sql — NOT applied by
// this lane), mirrored here in Drizzle and exported from index.ts.
// =============================================================================

/** Which surface a corrected claim came from — polymorphic, data not a
 *  schema fork. 'radar_axis' covers the job-side axis-weight correction
 *  (Class 4's scoped-in leg of axis-lens plan P5); 'gap' / 'reasoning_bullet'
 *  / 'through_line' / 'essay_line' are the other four named blast-radius
 *  surfaces. */
export type ClaimCorrectionSourceType =
  | 'gap'
  | 'radar_axis'
  | 'through_line'
  | 'essay_line'
  | 'reasoning_bullet';

/** 'disputed' — the person tapped "that's not right"; 'confirmed' — the
 *  person affirmed a claim the system was unsure about (the ASK-half's
 *  answer landing here too, since it's the same shape); 'withdrawn' — the
 *  person changed their mind about a PRIOR correction on this same
 *  (sourceType, sourceId) — a new terminal row, the old one stays queryable
 *  (no deletions, ever). */
export type ClaimCorrectionRuling = 'disputed' | 'confirmed' | 'withdrawn';

export const claimCorrections = pgTable(
  'claim_corrections',
  {
    id: id(),
    /** The real users.id uuid — declared FK, mirrors achievement_alignment_
     *  rulings.userId / axis_association_rulings.userId's own convention. */
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    /** Which surface/claim-family this correction is about. */
    sourceType: varchar('source_type', { length: 24 }).notNull(),
    /** Soft pointer into whatever table/id owns this claim — a job id for
     *  gap/radar_axis/through_line/reasoning_bullet claims (the honest
     *  granularity today: those four surfaces render bullets with NO
     *  per-bullet uuid of their own, only the owning JOB has a real id — this
     *  column stores that job id rather than inventing a synthetic per-bullet
     *  id), a facet/essay id for essay_line. No hard FK, same reasoning as
     *  achievement_alignment_rulings.achievementId's own header: source rows
     *  are superseded/regenerated, not updated in place, and a hard FK would
     *  either cascade-orphan a terminal correction or block the very
     *  regeneration the correction triggers. Because sourceId is often shared
     *  by MANY claims on one job, the exact claim is disambiguated by its own
     *  frozen flaggedText below — readers fold on (sourceId, flaggedText)
     *  together (claimCorrections.ts's claimFoldKey), never sourceId alone. */
    sourceId: uuid('source_id').notNull(),
    /** The exact claim text the person was shown, frozen at correction time
     *  — never re-derived from a later regeneration. Doubles as the second
     *  half of the (sourceId, flaggedText) fold key — see sourceId's comment. */
    flaggedText: text('flagged_text').notNull(),
    /** The person's own one-line correction note ("that's not right — I led
     *  that migration, didn't just support it"). Optional: a bare dispute
     *  with no note is still a valid, actionable signal. */
    userNote: text('user_note'),
    /** 'disputed' | 'confirmed' | 'withdrawn'. varchar, not a DB enum — see
     *  file header. */
    ruling: varchar('ruling', { length: 16 }).notNull(),
    /** Frozen context snapshot (e.g. { axisKey, weight } for radar_axis,
     *  { jobId, gapClass } for gap) — same evidenceSnapshot discipline as the
     *  two sibling ruling tables: written verbatim at correction time, never
     *  regenerated retroactively. */
    evidenceSnapshot: jsonb('evidence_snapshot'),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
  },
  (t) => ({
    userIdx: index('claim_corrections_user_idx').on(t.userId),
    sourceIdx: index('claim_corrections_source_idx').on(t.sourceType, t.sourceId),
  }),
);
